/**
 * confirm(message, options?) → Promise<boolean>
 *
 * Drop-in replacement for window.confirm that renders a shadcn AlertDialog
 * instead of a browser-native modal. Works anywhere — no provider needed.
 *
 * Usage:
 *   import { confirm } from "@/lib/confirm";
 *   if (!await confirm("Delete this item?")) return;
 *   if (!await confirm("Restore draft?", { title: "Restore version", confirmLabel: "Restore", destructive: false })) return;
 */
import React from "react";
import { createRoot } from "react-dom/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function confirm(
  message,
  {
    title = "Are you sure?",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    destructive = true,
  } = {},
) {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);

    const cleanup = (result) => {
      root.unmount();
      container.remove();
      resolve(result);
    };

    root.render(
      <AlertDialog
        defaultOpen
        onOpenChange={(open) => {
          if (!open) cleanup(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            <AlertDialogDescription>{message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => cleanup(false)}>
              {cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              className={
                destructive
                  ? "bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
                  : ""
              }
              onClick={() => cleanup(true)}
            >
              {confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>,
    );
  });
}
